import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "../../contexts/UserContext";
import { Spinner } from "../ui/spinner";
import { Skeleton, SkeletonCard } from "../ui/skeleton";
import { getApiUrl } from "../../config/api";
import { BusinessUnitService } from "../../services/businessUnitService";

const AutoRouteHandler = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const {
    isOrgAdmin,
    isBusinessUnitHead,
    isBusinessUser,
    primaryRole,
    userProfile,
  } = useUser();

  const [isRouting, setIsRouting] = useState(true);
  const [hasCheckedRouting, setHasCheckedRouting] = useState(false);

  useEffect(() => {
    const handleAutoRouting = async () => {
      if (!userProfile || hasCheckedRouting) return;

      try {
        setIsRouting(true);

        // Handle Organization Admins
        if (isOrgAdmin) {
          // Fetch organizations to check if user has only one
          const response = await fetch(getApiUrl("organizations"), {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          });

          if (response.ok) {
            const organizations = await response.json();

            if (organizations.length === 1) {
              navigate(`/organization/${organizations[0].id}/dashboard`, {
                replace: true,
              });
              return;
            }
          }

          // Multiple organizations or error - show selection page
          navigate("/organizations", { replace: true });
          return;
        }

        // Handle Business Unit Heads and Business Users
        if (isBusinessUnitHead || isBusinessUser) {
          // Fetch business units to check if user has only one
          const businessUnits = await BusinessUnitService.getMyBusinessUnits(
            getAccessTokenSilently
          );

          if (businessUnits.length === 1) {
            navigate(`/business-unit/${businessUnits[0].id}/dashboard`, {
              replace: true,
            });
            return;
          }

          // Multiple business units or error - show selection page
          navigate("/business-units", { replace: true });
          return;
        }

        // Default case - navigate based on primary role
        const defaultRoute = getDefaultRoute();
        navigate(defaultRoute, { replace: true });
      } catch (error) {
        console.error("Error in auto-routing:", error);
        // Fallback to default route
        const defaultRoute = getDefaultRoute();
        navigate(defaultRoute, { replace: true });
      } finally {
        setIsRouting(false);
        setHasCheckedRouting(true);
      }
    };

    handleAutoRouting();
  }, [
    userProfile,
    hasCheckedRouting,
    isOrgAdmin,
    isBusinessUnitHead,
    isBusinessUser,
    navigate,
    getAccessTokenSilently,
  ]);

  const getDefaultRoute = () => {
    switch (primaryRole) {
      case "org_admin":
        return "/organizations";
      case "business_unit_head":
        return "/business-units";
      case "business_user":
        return "/business-units";
      default:
        return "/profile";
    }
  };

  // Show loading skeleton while checking routing
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="text-center space-y-6 w-full max-w-md">
        <SkeletonCard className="p-8">
          <div className="space-y-4">
            <Skeleton className="h-12 w-12 mx-auto rounded-full" />
            <Skeleton className="h-4 w-48 mx-auto" />
            <Skeleton className="h-3 w-32 mx-auto" />
          </div>
        </SkeletonCard>
        <p className="text-muted-foreground">Setting up your dashboard...</p>
      </div>
    </div>
  );
};

export default AutoRouteHandler;
